export type DescriptionListItem = { text: string; children: string[] };

export type DescriptionBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; lines: string[] }
  | { type: 'list'; items: DescriptionListItem[] };

const BULLET_RE = /^([-•*])\s+(.*)$/;
const HEADING_MAX_LENGTH = 40;

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Parse free-form admin-entered description text (often pasted from flyers)
 * into headings, paragraphs, and lists. Mixed bullet markers (-, •, *) are
 * supported; a second marker style inside a list nests under the previous item.
 */
export function parseEventDescription(raw: string | null | undefined): DescriptionBlock[] {
  const blocks: DescriptionBlock[] = [];
  const state: {
    paragraph: string[] | null;
    list: { marker: string; items: DescriptionListItem[] } | null;
  } = { paragraph: null, list: null };

  const flush = () => {
    if (state.paragraph) blocks.push({ type: 'paragraph', lines: state.paragraph });
    if (state.list) blocks.push({ type: 'list', items: state.list.items });
    state.paragraph = null;
    state.list = null;
  };

  for (const rawLine of (raw || '').replace(/\r\n?/g, '\n').split('\n')) {
    const text = collapseWhitespace(rawLine);
    if (!text) {
      flush();
      continue;
    }

    const bullet = BULLET_RE.exec(text);
    if (bullet) {
      const [, marker, itemText] = bullet;
      if (!state.list) {
        flush();
        state.list = { marker, items: [{ text: itemText, children: [] }] };
      } else if (marker === state.list.marker) {
        state.list.items.push({ text: itemText, children: [] });
      } else {
        state.list.items[state.list.items.length - 1].children.push(itemText);
      }
      continue;
    }

    if (state.list && /^\s/.test(rawLine)) {
      const last = state.list.items[state.list.items.length - 1];
      if (last.children.length) {
        last.children[last.children.length - 1] += ` ${text}`;
      } else {
        last.text += ` ${text}`;
      }
      continue;
    }

    if (text.endsWith(':') && text.length <= HEADING_MAX_LENGTH) {
      flush();
      blocks.push({ type: 'heading', text: text.slice(0, -1).trim() });
      continue;
    }

    if (state.list) flush();
    state.paragraph = state.paragraph ?? [];
    state.paragraph.push(text);
  }

  flush();
  return blocks;
}

/**
 * Short plain-text teaser from the description's opening paragraph, without a
 * trailing list intro such as "This event will include:".
 */
export function summarizeEventDescription(
  raw: string | null | undefined,
  maxLength = 200
): string {
  const first = parseEventDescription(raw)[0];
  if (!first) return '';

  let text: string;
  if (first.type === 'heading') {
    text = first.text;
  } else if (first.type === 'list') {
    text = first.items.map((item) => item.text).join(', ');
  } else {
    const joined = first.lines.join(' ');
    text = joined.replace(/(^|[.!?])\s*[^.!?]*:$/, '$1').trim() || joined;
  }
  if (text.length <= maxLength) return text;

  const cut = text.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.]+$/, '')}…`;
}
